class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.activeCellIndex = 0;
    this.input.setAttribute('aria-haspopup', 'grid');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    this.clearSelection();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[role="row"][aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    // Mouse-focused controls retain their own native keyboard activation.
    if (event.target !== this.input || event.isComposing) return;
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'ArrowLeft':
        this.switchCell(-1);
        break;
      case 'ArrowRight':
        this.switchCell(1);
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  onKeydown(event) {
    if (event.target !== this.input || event.isComposing) return;
    const selectedRow = this.querySelector('[role="row"][aria-selected="true"]');
    const verticalArrow = event.code === 'ArrowUp' || event.code === 'ArrowDown';
    const horizontalArrow = event.code === 'ArrowLeft' || event.code === 'ArrowRight';
    if (verticalArrow || (horizontalArrow && selectedRow) || (event.code === 'Enter' && selectedRow) || event.code === 'Escape') {
      event.preventDefault();
    }
    if (event.code === 'Tab') this.close();
    // Editing resumes in the text field instead of retaining a stale grid cell.
    if (selectedRow && (event.key?.length === 1 || ['Backspace', 'Delete', 'Home', 'End'].includes(event.code))) {
      this.clearSelection();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  getRowCells(row) {
    return Array.from(row.querySelectorAll('[role="gridcell"]')).filter((cell) => cell.offsetParent !== null);
  }

  clearSelection() {
    this.querySelectorAll('[role="row"][aria-selected="true"]').forEach((row) => row.setAttribute('aria-selected', 'false'));
    this.querySelectorAll('.predictive-search__cell--active').forEach((cell) => cell.classList.remove('predictive-search__cell--active'));
    this.input.setAttribute('aria-activedescendant', '');
    this.activeCellIndex = 0;
  }

  setActiveCell(row, index) {
    const cells = this.getRowCells(row);
    if (!cells.length) return;
    this.querySelectorAll('.predictive-search__cell--active').forEach((cell) => cell.classList.remove('predictive-search__cell--active'));
    this.activeCellIndex = Math.max(0, Math.min(index, cells.length - 1));
    const cell = cells[this.activeCellIndex];
    cell.classList.add('predictive-search__cell--active');
    this.input.setAttribute('aria-activedescendant', cell.id);
    cell.scrollIntoView({ block: 'nearest' });
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const rows = Array.from(this.querySelectorAll('[role="row"]')).filter((row) => row.offsetParent !== null);
    if (!rows.length) return;
    const selectedRow = this.querySelector('[role="row"][aria-selected="true"]');
    const selectedIndex = rows.indexOf(selectedRow);
    let nextIndex = selectedIndex + (direction === 'up' ? -1 : 1);
    if (selectedIndex === -1) nextIndex = direction === 'up' ? rows.length - 1 : 0;
    if (nextIndex < 0 || nextIndex >= rows.length) return;
    if (this.statusElement) this.statusElement.textContent = '';
    if (selectedRow) selectedRow.setAttribute('aria-selected', 'false');
    const row = rows[nextIndex];
    row.setAttribute('aria-selected', 'true');
    this.setActiveCell(row, this.activeCellIndex);
  }

  switchCell(direction) {
    if (!this.getAttribute('open')) return;
    const row = this.querySelector('[role="row"][aria-selected="true"]');
    if (row) this.setActiveCell(row, this.activeCellIndex + direction);
  }

  selectOption() {
    const cell = this.querySelector('.predictive-search__cell--active');
    const action = cell?.querySelector('a, button');
    if (action && !action.disabled && action.getAttribute('aria-disabled') !== 'true') action.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.clearSelection();
    this.querySelectorAll('[role="row"]').forEach((row) => {
      row.querySelectorAll('[role="gridcell"]').forEach((cell, index) => {
        cell.id = `${row.id}-cell-${index + 1}`;
      });
    });
    // The combobox retains DOM focus while arrow keys move through the grid.
    this.predictiveSearchResults.querySelectorAll('a, button').forEach((control) => control.setAttribute('tabindex', '-1'));
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    this.clearSelection();
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

